/**
 * CertFileForm — sekcja "Plik certyfikatu" z osobnym buttonem "Zapisz certyfikat".
 *
 * Część split-upload UI (refaktor 2026-06-01): user widzi 3 osobne sekcje
 * (cert / klucz / hasło), każdą zapisuje niezależnie.
 *
 * Props:
 *   - adminConfig (object)  — bieżący stan z /admin/config (do statusu)
 *   - onSaved (callback)    — wywołane po pomyślnym zapisie
 */
import { useState, useRef } from "react";
import { logger } from "../../../utils/logger";
import { toast } from "sonner";
import { Save, Upload, FileCheck, CheckCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { uploadCertFile } from "../services/ksefApi";
import { safeDetailMessage, safeErrorMessage } from "../utils/errorMessages";

export const CertFileForm = ({ adminConfig, onSaved }) => {
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const handleSave = async () => {
    if (!file) {
      toast.error("Wybierz plik certyfikatu");
      return;
    }
    setSaving(true);
    try {
      await uploadCertFile(file);
      toast.success("Certyfikat zapisany");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (onSaved) onSaved();
    } catch (err) {
      logger.error("Error saving cert:", err);
      toast.error(
        safeDetailMessage(err.response) || safeErrorMessage(err) || "Błąd zapisywania certyfikatu",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-3" data-testid="cert-file-section">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            Plik certyfikatu <span className="text-red-500">*</span>
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Wymagany. Akceptowane formaty: .pem, .crt, .p12, .pfx
          </p>
        </div>
        {adminConfig?.has_cert && (
          <div className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
            <CheckCircle className="w-3 h-3" />
            Wgrany
          </div>
        )}
      </div>

      {adminConfig?.cert_filename && !file && (
        <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 rounded px-3 py-2">
          <FileCheck className="w-4 h-4 text-green-600" />
          <span className="font-mono break-all">{adminConfig.cert_filename}</span>
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        accept=".pem,.crt,.cer,.p12,.pfx"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="hidden"
        data-testid="cert-file-input"
      />

      {file ? (
        <div className="flex items-center justify-between gap-2 bg-blue-50 border border-blue-200 rounded px-3 py-2">
          <div className="flex items-center gap-2 text-sm text-blue-900 min-w-0">
            <Upload className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{file.name}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              setFile(null);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
            className="text-blue-700 hover:text-blue-900"
            data-testid="cert-file-clear-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          className="w-full"
          data-testid="cert-file-pick-btn"
        >
          <Upload className="w-4 h-4 mr-2" />
          {adminConfig?.has_cert ? "Wybierz nowy plik certyfikatu" : "Wybierz plik certyfikatu"}
        </Button>
      )}

      <Button
        onClick={handleSave}
        disabled={!file || saving}
        className="w-full"
        data-testid="cert-file-save-btn"
      >
        {saving ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
        ) : (
          <Save className="w-4 h-4 mr-2" />
        )}
        Zapisz certyfikat
      </Button>
    </div>
  );
};

export default CertFileForm;
